import { useNavigate } from "@tanstack/react-router";
import {
  ClipboardCheck,
  ClipboardList,
  FileText,
  Landmark,
  Scale,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Chip } from "@/ui/primitives";
import { formatBRL, formatDateBR } from "@/lib/format";
import type { ClienteDetail } from "../schemas";
import { parseOnusItems, type OnusItem } from "../onus";

interface Props {
  cliente: ClienteDetail;
  clienteId: string;
}

export interface OnusRow {
  id: string;
  tipo: "Próprio" | "Arrendado";
  matricula: string | null;
  imovel: string | null;
  cartorio: string | null;
  municipio: string | null;
  uf: string | null;
  proprietario_nome: string | null;
  proprietario_cpf: string | null;
  credor: string;
  onus_gravame: string;
  status: OnusItem["status"];
  vencimento: string | null;
  instrumento: string;
  observacoes: string;
  item: OnusItem;
}

export function formatOnusInstrumento(item: OnusItem): string {
  if (item.instrumento.trim()) return item.instrumento;
  return [
    item.cedula ? `Cédula ${item.cedula}` : null,
    typeof item.valor === "number" ? formatBRL(item.valor) : null,
    item.grau ? `${item.grau} grau` : null,
    item.ato_registral || item.ato || null,
  ]
    .filter(Boolean)
    .join(" · ");
}

export function formatOnusObservacoes(item: OnusItem): string {
  if (item.observacoes.trim()) return item.observacoes;
  const detalhes = item.atos.map((ato) => ato.descricao.trim()).filter(Boolean);
  if (item.origem_documento) detalhes.push(`Origem: ${item.origem_documento}`);
  return detalhes.join("\n");
}

export function buildOnusRows(cliente: ClienteDetail): OnusRow[] {
  const rows: OnusRow[] = [];

  for (const im of cliente.imoveis) {
    const items = parseOnusItems((im as Record<string, unknown>).onus_items);
    for (const item of items) {
      rows.push({
        id: `proprio:${String(im.id ?? im.matricula ?? "sem-imovel")}:${item.id}`,
        tipo: "Próprio",
        matricula: im.matricula ?? null,
        imovel: im.denominacao ?? null,
        cartorio: typeof im.cartorio === "string" ? im.cartorio : null,
        municipio: im.municipio ?? cliente.municipio ?? null,
        uf: im.uf ?? cliente.uf ?? null,
        proprietario_nome:
          typeof im.proprietario_nome === "string" ? im.proprietario_nome : cliente.nome,
        proprietario_cpf:
          typeof im.proprietario_cpf === "string" ? im.proprietario_cpf : cliente.cpf,
        credor: item.credor,
        onus_gravame: item.tipo,
        status: item.status,
        vencimento: item.vencimento ?? null,
        instrumento: formatOnusInstrumento(item),
        observacoes: formatOnusObservacoes(item),
        item,
      });
    }
  }

  for (const arr of cliente.arrendamentos) {
    const imovel = arr.imovel as Record<string, unknown> | null | undefined;
    if (!imovel) continue;
    const items = parseOnusItems(imovel.onus_items);
    for (const item of items) {
      rows.push({
        id: `arrendado:${String(imovel.id ?? arr.imovel_id ?? arr.id ?? "sem-imovel")}:${item.id}`,
        tipo: "Arrendado",
        matricula:
          (imovel.matricula as string | null) ?? arr.imovel_matricula ?? null,
        imovel:
          (imovel.denominacao as string | null) ??
          arr.imovel_denominacao ??
          null,
        cartorio: (imovel.cartorio as string | null) ?? null,
        municipio: (imovel.municipio as string | null) ?? cliente.municipio ?? null,
        uf: (imovel.uf as string | null) ?? cliente.uf ?? null,
        proprietario_nome:
          (imovel.proprietario_nome as string | null) ?? cliente.nome ?? null,
        proprietario_cpf:
          (imovel.proprietario_cpf as string | null) ?? cliente.cpf ?? null,
        credor: item.credor,
        onus_gravame: item.tipo,
        status: item.status,
        vencimento: item.vencimento ?? null,
        instrumento: formatOnusInstrumento(item),
        observacoes: formatOnusObservacoes(item),
        item,
      });
    }
  }

  return rows;
}

export function onusStatusTone(
  status: OnusItem["status"],
): "warn" | "ok" | "neutral" {
  if (status === "vencido_sem_baixa") return "warn";
  if (status === "vigente") return "ok";
  return "neutral";
}

function normalize(s: string): string {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
}

export function isLivreDesembaracado(text: string): boolean {
  return normalize(text) === "livre e desembaracado";
}

export function onusStatusLabel(status: OnusItem["status"]): string {
  if (status === "vencido_sem_baixa") return "Necessita de baixa";
  if (status === "vigente") return "Vigente";
  return "Baixado";
}

export function canSelectOnusRow(
  row: OnusRow,
  selectionMatricula?: string | null,
): boolean {
  return Boolean(
    row.status === "vencido_sem_baixa" &&
      row.matricula &&
      (!selectionMatricula || row.matricula === selectionMatricula),
  );
}

export function OnusGravamesTable({
  rows,
  selectedIds,
  selectionMatricula,
  onToggleSelection,
}: {
  rows: OnusRow[];
  selectedIds?: Set<string>;
  selectionMatricula?: string | null;
  onToggleSelection?: (row: OnusRow) => void;
}) {
  const selectable = Boolean(selectedIds && onToggleSelection);

  return (
    <div className="overflow-x-auto">
      <table className="table table-sm w-full">
        <thead>
          <tr>
            {selectable && <th className="no-print text-center">Selecionar</th>}
            <th>Tipo</th>
            <th>Matrícula</th>
            <th>Imóvel</th>
            <th>Credor</th>
            <th>Ônus / Gravame</th>
            <th>Status</th>
            <th>Vencimento</th>
            <th>Instrumento e Observações</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              {selectable && (
                <td className="no-print text-center">
                  {row.status === "vencido_sem_baixa" ? (
                    <input
                      type="checkbox"
                      checked={selectedIds?.has(row.id) ?? false}
                      disabled={!canSelectOnusRow(row, selectionMatricula)}
                      onChange={() => onToggleSelection?.(row)}
                      title={
                        !row.matricula
                          ? "Informe a matrícula antes de emitir a solicitação"
                          : selectionMatricula && row.matricula !== selectionMatricula
                            ? `A seleção atual pertence à matrícula ${selectionMatricula}`
                            : "Selecionar este registro para a solicitação de baixa"
                      }
                      aria-label={`Selecionar registro da matrícula ${row.matricula ?? "não informada"}`}
                      className="size-4 cursor-pointer accent-[var(--accent)] disabled:cursor-not-allowed disabled:opacity-40"
                    />
                  ) : (
                    <span className="text-text-muted">—</span>
                  )}
                </td>
              )}
              <td>
                <Chip tone={row.tipo === "Próprio" ? "ok" : "accent"} size="sm">
                  {row.tipo}
                </Chip>
              </td>
              <td>
                {row.matricula ? (
                  <Chip tone="info" size="sm">
                    {row.matricula}
                  </Chip>
                ) : (
                  <span
                    className="text-text-muted"
                    style={{ fontSize: "0.78rem" }}
                  >
                    —
                  </span>
                )}
              </td>
              <td style={{ fontSize: "0.82rem" }}>{row.imovel ?? "—"}</td>
              <td style={{ fontSize: "0.82rem" }}>{row.credor || "—"}</td>
              <td style={{ fontSize: "0.82rem" }}>{row.onus_gravame || "—"}</td>
              <td>
                <Chip tone={onusStatusTone(row.status)} size="sm">
                  {onusStatusLabel(row.status)}
                </Chip>
              </td>
              <td>
                {row.vencimento ? (
                  <Chip
                    tone={
                      row.status === "vencido_sem_baixa" ? "warn" : "neutral"
                    }
                    size="sm"
                  >
                    {formatDateBR(row.vencimento)}
                  </Chip>
                ) : (
                  <span
                    className="text-text-muted"
                    style={{ fontSize: "0.78rem" }}
                  >
                    —
                  </span>
                )}
              </td>
              <td
                className="align-top"
                style={{ fontSize: "0.82rem", minWidth: 280, maxWidth: 380 }}
              >
                {!row.instrumento && !row.observacoes ? (
                  <span className="text-text-muted">—</span>
                ) : (
                  <div className="flex flex-col gap-1.5 py-1">
                    {row.instrumento && (
                      <div className="flex flex-col gap-0.5">
                        <span
                          className="uppercase font-semibold text-text-muted"
                          style={{
                            fontSize: "0.66rem",
                            letterSpacing: "0.06em",
                          }}
                        >
                          Instrumento
                        </span>
                        <span className="text-text whitespace-pre-wrap break-words leading-snug">
                          {row.instrumento}
                        </span>
                      </div>
                    )}
                    {row.observacoes && (
                      <div className="flex flex-col gap-0.5">
                        <span
                          className="uppercase font-semibold text-text-muted"
                          style={{
                            fontSize: "0.66rem",
                            letterSpacing: "0.06em",
                          }}
                        >
                          Observações
                        </span>
                        {isLivreDesembaracado(row.observacoes) ? (
                          <Chip tone="ok" size="sm" className="self-start">
                            {row.observacoes}
                          </Chip>
                        ) : (
                          <span className="text-text-2 whitespace-pre-wrap break-words leading-snug">
                            {row.observacoes}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Card de relatório (design ARA): chip de ícone coral + nome, navegável.
function ReportCard({
  icon: Icon,
  title,
  onClick,
}: {
  icon: LucideIcon;
  title: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-[150px] w-[196px] flex-col items-center justify-center gap-3 rounded-[14px] border border-border bg-surface p-4 text-center transition-colors hover:bg-[var(--nav-active-bg)] focus-visible:bg-[var(--nav-active-bg)]"
    >
      <span className="grid size-12 shrink-0 place-items-center rounded-xl bg-accent">
        <Icon size={24} strokeWidth={2} className="text-[var(--on-solid)]" />
      </span>
      <span className="text-[15px] font-semibold leading-snug text-text">
        {title}
      </span>
    </button>
  );
}

export function ClienteRelatoriosTab({ cliente: _cliente, clienteId }: Props) {
  const navigate = useNavigate();

  return (
    <div className="flex flex-wrap gap-4 font-ui">
      <ReportCard
        icon={Landmark}
        title="Operações de Crédito"
        onClick={() =>
          void navigate({
            to: "/clientes/$id/relatorio/operacoes-credito",
            params: { id: clienteId },
          })
        }
      />
      <ReportCard
        icon={Scale}
        title="Ônus e Gravames"
        onClick={() =>
          void navigate({
            to: "/clientes/$id/relatorio/onus-gravames",
            params: { id: clienteId },
          })
        }
      />
      <ReportCard
        icon={ClipboardCheck}
        title="Conformidade Documental"
        onClick={() =>
          void navigate({
            to: "/clientes/$id/relatorio/conformidade-documental",
            params: { id: clienteId },
          })
        }
      />
      <ReportCard
        icon={ClipboardList}
        title="Conformidade Documental Resumido"
        onClick={() =>
          void navigate({
            to: "/clientes/$id/relatorio/conformidade-resumido",
            params: { id: clienteId },
          })
        }
      />
      <ReportCard
        icon={FileText}
        title="Orçamento"
        onClick={() =>
          void navigate({
            to: "/clientes/$id/relatorio/orcamento",
            params: { id: clienteId },
          })
        }
      />
    </div>
  );
}
